import type { ReviewReportFullResponseType } from '@readme/api/src/mappings/branch/reviewReport/types';

import React, { useEffect } from 'react';

import useReadmeApi from '@core/hooks/useReadmeApi';
import type { APIErrorType } from '@core/types';

import { useSuperHubStore } from '..';

export function InitializeReviewReport({ children }: { children: React.ReactNode }) {
  const [apiBaseUrl, currentBranch, isReviewReportReady, updateReviewReport, isReviewActionsEnabled] = useSuperHubStore(
    s => [
      s.getApiBaseUrl(),
      s.getCurrentChildBranch(),
      s.branches.reviewReport?.status === 'completed' || s.branches.reviewReport?.status === 'failed',
      s.branches.updateReviewReport,
      s.isSuperHubBranchReviewActionsEnabled,
    ],
  );

  /**
   * Indicates that the current branch has a review report to poll until it's
   * completed or failed. When no review report is present, no polling requests
   * should be made.
   */
  const isCurrentBranchReportAvailable = !!currentBranch?.review.report.id;

  const url =
    isReviewActionsEnabled && isCurrentBranchReportAvailable
      ? `/review/reports?report_id=${currentBranch.review.report.id}`
      : null;
  const { data } = useReadmeApi<ReviewReportFullResponseType, APIErrorType>(url, {
    apiBaseUrl,
    swr: {
      // Poll endpoint every X seconds until report is ready.
      refreshInterval: isReviewReportReady ? 0 : 5000,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  });

  useEffect(() => {
    if (!data) return;
    updateReviewReport(data.data);
  }, [data, updateReviewReport]);

  return <>{children}</>;
}
